import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/icon-v2-512.png";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      // In demo mode, we show the token
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      toast.success('Password reset instructions sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container px-4" style={{ background: 'linear-gradient(180deg, #0B1F3A 0%, #1a3a8f 50%, #F2F5F7 50%)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="AegisPay Logo" 
            className="w-20 h-20 mx-auto mb-4 rounded-2xl object-cover shadow-lg"
          />
          <h1 className="text-3xl font-bold text-white">Aegis<span className="text-[#1E5EFF]">Pay</span></h1>
          <p className="text-blue-200 mt-1">Save smarter with group savings</p>
          <p className="text-blue-200/70 italic text-[11px] mt-1">Your money follows rules, not promises</p>
        </div>

        <Card className="glass-card border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-[#0B1F3A]">
              {sent ? 'Check Your Email' : 'Forgot Password'}
            </CardTitle>
            <CardDescription>
              {sent 
                ? 'We sent you password reset instructions' 
                : 'Enter your email to reset your password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!sent ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 rounded-xl bg-slate-50 pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white font-medium btn-press"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                  <p className="text-emerald-700">
                    If an account exists with that email, you'll receive reset instructions.
                  </p>
                </div>

                {/* Demo mode: show reset token */}
                {resetToken && (
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <p className="text-amber-700 text-sm mb-2">
                      <span className="demo-badge mr-2">DEMO</span>
                      Reset token (for testing):
                    </p>
                    <code className="block p-2 bg-white rounded text-sm break-all">
                      {resetToken}
                    </code>
                    <Button
                      onClick={() => navigate(`/reset-password?token=${resetToken}`)}
                      className="w-full mt-3 bg-amber-500 hover:bg-amber-600"
                      data-testid="use-token-button"
                    >
                      Use This Token
                    </Button>
                  </div>
                )}

                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="w-full h-12 rounded-full"
                >
                  Back to Login
                </Button>
              </div>
            )}

            {!sent && (
              <div className="mt-6 text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-[#2272C5] font-medium hover:underline flex items-center justify-center gap-1"
                >
                  <ArrowLeft size={14} />
                  Back to Login
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
